import React from "react";
import { AlertTriangle } from "lucide-react";

function Empty({ msg }) {
  return <div className="text-sm text-gray-500 text-center py-8">{msg}</div>;
}

export default function ReconciliationView({ rec, fileName, uploadedAt, appliedAt }) {
  if (!rec) {
    return <Empty msg="No reconciliation report yet. Upload an FRS to generate one." />;
  }
  const totalDelta = (rec.rooms_total_qty || 0) - (rec.products_total_target || 0);
  return (
    <div className="space-y-3">
      <div className="border border-gray-200 rounded-md px-3 py-2 flex items-center gap-3 text-xs flex-wrap">
        <span className="text-gray-500 uppercase tracking-wide font-medium">Total accessories</span>
        <span className="text-sm font-semibold text-gray-900">{rec.rooms_total_qty || 0}</span>
        <span className="text-gray-400">vs Products tab target</span>
        <span className="text-sm font-medium text-gray-700">{rec.products_total_target || 0}</span>
        {totalDelta !== 0 && (
          <span className={`text-xs px-1.5 py-0.5 rounded-full border ${totalDelta > 0 ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-orange-50 border-orange-200 text-orange-700"}`}>
            Δ {totalDelta > 0 ? "+" : ""}{totalDelta}
          </span>
        )}
        {fileName && <span className="ml-auto text-[11px] text-gray-500">{fileName}{appliedAt && ` · applied ${new Date(appliedAt).toLocaleString()}`}</span>}
      </div>

      <div>
        <div className="text-xs text-gray-600 font-medium mb-1">Per-product (Rooms tab vs Products tab)</div>
        <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto">
          {(rec.per_product || []).length === 0 ? <Empty msg="No products parsed." /> : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-gray-500"><th className="text-left px-2 py-1 font-medium">Part</th><th className="text-left px-2 py-1 font-medium">Description</th><th className="text-right px-2 py-1 font-medium">Rooms tab</th><th className="text-right px-2 py-1 font-medium">Products tab</th><th className="text-right px-2 py-1 font-medium">Δ</th></tr>
              </thead>
              <tbody>
                {rec.per_product.map((p, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-2 py-1 text-gray-500">{p.code || "—"}</td>
                    <td className="px-2 py-1 text-gray-900">{p.description}</td>
                    <td className="px-2 py-1 text-right font-medium">{p.rooms_qty}</td>
                    <td className="px-2 py-1 text-right text-gray-700">{p.target_qty == null ? "—" : p.target_qty}</td>
                    <td className={`px-2 py-1 text-right font-medium ${p.delta == null ? "text-gray-400" : p.delta === 0 ? "text-emerald-700" : "text-amber-700"}`}>
                      {p.delta == null ? "n/a" : (p.delta === 0 ? "✓" : (p.delta > 0 ? `+${p.delta}` : p.delta))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(rec.row_anomalies || []).length > 0 && (
        <div>
          <div className="text-xs text-gray-600 font-medium mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-600" />
            Row anomalies ({rec.row_anomalies.length}) — products sum ≠ "Number of Accessories" column
          </div>
          <div className="border border-amber-200 bg-amber-50/40 rounded-md max-h-40 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-amber-50 sticky top-0">
                <tr className="text-amber-800"><th className="text-left px-2 py-1 font-medium">Row</th><th className="text-left px-2 py-1 font-medium">Location</th><th className="text-right px-2 py-1 font-medium">Σ products</th><th className="text-right px-2 py-1 font-medium"># Accessories</th><th className="text-right px-2 py-1 font-medium">Δ</th></tr>
              </thead>
              <tbody>
                {rec.row_anomalies.map((a, i) => (
                  <tr key={i} className="border-t border-amber-100">
                    <td className="px-2 py-1 text-gray-500">{a.row_index}</td>
                    <td className="px-2 py-1 text-gray-900">{a.building} · F{a.floor} · Rm {a.room_number}</td>
                    <td className="px-2 py-1 text-right">{a.products_sum}</td>
                    <td className="px-2 py-1 text-right">{a.number_of_accessories}</td>
                    <td className={`px-2 py-1 text-right font-medium ${a.delta === 0 ? "text-emerald-700" : "text-amber-700"}`}>{a.delta > 0 ? `+${a.delta}` : a.delta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(rec.skipped_rows || []).length > 0 && (
        <details className="text-xs text-gray-600">
          <summary className="cursor-pointer hover:text-gray-900">Skipped totals rows ({rec.skipped_rows.length})</summary>
          <ul className="mt-1 ml-4 list-disc">
            {rec.skipped_rows.map((s, i) => (
              <li key={i}>Row {s.row_index} — {s.reason}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
